import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslation } from "@/hooks/useTranslation";
import { formatDateNumeric } from "@/lib/format-amount";
import { AdminTeamDTO, Nullable } from "@shaarp/common";
import { InfoIcon } from "lucide-react";
import { useState } from "react";
import { DialogAdminTeamLicenseInventory } from "./dialog-admin-team-license-inventory";

/**
 * "Subscription" cell of the admin teams table: renders a compact stacked bar
 * breaking down the team's monthly vs annual licenses and opens the license
 * inventory dialog on click.
 */
export default function DataTableCellAdminsLicenseInventory({
  team,
}: {
  team: AdminTeamDTO;
}) {
  const { t, i18n } = useTranslation();

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const monthlyCount = team.purchasedProMonthly ?? 0;
  const annualCount = team.purchasedProAnnual ?? 0;
  const total = monthlyCount + annualCount;

  const monthlyPct = total > 0 ? (monthlyCount / total) * 100 : 0;
  const annualPct = total > 0 ? (annualCount / total) * 100 : 0;

  // A schedule only exists once a frequency carries a target, which is how the
  // inventory API decides a scheduled change is pending. A full
  // cancellation creates no schedule at all, so it is read off the
  // subscription's end date instead and drops the whole pool.
  const scheduledRemoval = (() => {
    let seats = 0;
    let effectiveAt: Date | null = null;

    const collect = (pool: {
      purchased: number;
      scheduledPro: Nullable<number>;
      endsAt: Nullable<Date>;
    }) => {
      const scheduled = pool.endsAt ? 0 : pool.scheduledPro;
      if (scheduled === null || scheduled >= pool.purchased) return;
      seats += pool.purchased - scheduled;
      // The team-level effective date only tracks schedules, so a cancellation
      // dates from its own subscription instead.
      const raw = pool.endsAt ?? team.scheduledChangeEffectiveAt;
      const date = raw ? new Date(raw) : null;
      if (date && (!effectiveAt || date < effectiveAt)) effectiveAt = date;
    };

    collect({
      purchased: monthlyCount,
      scheduledPro: team.scheduledProMonthly,
      endsAt: team.stripeMonthlySubscriptionEndsAt,
    });
    collect({
      purchased: annualCount,
      scheduledPro: team.scheduledProAnnual,
      endsAt: team.stripeAnnualSubscriptionEndsAt,
    });

    return seats > 0 ? { seats, effectiveAt } : null;
  })();

  const scheduledDate = formatDateNumeric(
    scheduledRemoval?.effectiveAt ?? null,
    i18n.language
  );

  // A team still in setup owns no purchased stock yet: its seats are the starting
  // Pro plan the admin defined, billed once the client finalizes the checkout.
  const plannedAnnualCount = team.initialProLicenseCountAnnual;
  const plannedMonthlyCount = team.initialProLicenseCountMonthly;
  const plannedCount = plannedAnnualCount ?? plannedMonthlyCount ?? 0;
  const hasPlannedLicenses = !team.setupCompleted && plannedCount > 0;

  return (
    <div className="flex w-40 flex-col gap-1">
      <button
        type="button"
        onClick={() => setIsDialogOpen(true)}
        className="flex w-full flex-col gap-1.5 rounded-md p-1 text-left transition-colors hover:bg-muted"
      >
        {total === 0 ? (
          hasPlannedLicenses ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-help text-sm text-muted-foreground">
                  {t(
                    plannedAnnualCount !== null
                      ? "admin.dataTableAdminTeams.licenseBreakdown.plannedAnnual"
                      : "admin.dataTableAdminTeams.licenseBreakdown.plannedMonthly",
                    { count: plannedCount }
                  )}
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>
                  {t(
                    "admin.dataTableAdminTeams.licenseBreakdown.plannedTooltip"
                  )}
                </p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <span className="text-sm text-muted-foreground">
              {t("admin.dataTableAdminTeams.licenseBreakdown.none")}
            </span>
          )
        ) : (
          <>
            <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-secondary">
              {monthlyCount > 0 && (
                <div
                  className="h-full bg-majorelle"
                  style={{ width: `${monthlyPct}%` }}
                />
              )}
              {annualCount > 0 && (
                <div
                  className="h-full bg-emerald-500"
                  style={{ width: `${annualPct}%` }}
                />
              )}
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              {monthlyCount > 0 && (
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-majorelle" />
                  {t("admin.dataTableAdminTeams.licenseBreakdown.monthly", {
                    count: monthlyCount,
                  })}
                </span>
              )}
              {annualCount > 0 && (
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  {t("admin.dataTableAdminTeams.licenseBreakdown.annual", {
                    count: annualCount,
                  })}
                </span>
              )}
            </div>
          </>
        )}
      </button>
      {scheduledRemoval && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="flex cursor-help items-center gap-1 px-1 text-xs font-medium text-destructive">
              <InfoIcon className="h-3 w-3 shrink-0" />
              {t(
                "admin.dataTableAdminTeams.licenseBreakdown.scheduledRemoval",
                { count: scheduledRemoval.seats }
              )}
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <p>
              {t(
                "admin.dataTableAdminTeams.licenseBreakdown.scheduledRemovalTooltip",
                { count: scheduledRemoval.seats, date: scheduledDate }
              )}
            </p>
          </TooltipContent>
        </Tooltip>
      )}
      {isDialogOpen && (
        <DialogAdminTeamLicenseInventory
          team={team}
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
        />
      )}
    </div>
  );
}
